/**
 * Shared value formatter used by Chart tooltips/labels, TanTable cells,
 * and anywhere else a declarative format spec needs to produce a display string.
 */

export type AtlasValueFormat = {
  style?: "currency" | "number" | "int" | "decimal" | "percent" | "compact" | "date" | "datetime" | "time";
  currency?: string;
  decimals?: number;
  percentScale?: "unit" | "whole";
  dateFormat?: string;
  nullDisplay?: string;
  prefix?: string;
  suffix?: string;
};

export function buildFormatter(fmt: AtlasValueFormat): (value: unknown) => string {
  const style = fmt.style;
  const currency = fmt.currency ?? "USD";
  const decimals = typeof fmt.decimals === "number"
    ? fmt.decimals
    : (style === "int" ? 0 : style === "currency" ? 2 : style === "percent" ? 1 : 2);
  const nullDisplay = fmt.nullDisplay ?? "";
  const prefix = fmt.prefix ?? "";
  const suffix = fmt.suffix ?? "";
  const wrap = (s: string) => `${prefix}${s}${suffix}`;

  const numFmt = (v: unknown, nf: Intl.NumberFormat) => {
    if (v == null) return nullDisplay;
    if (typeof v === "number") return wrap(nf.format(v));
    const n = Number(v);
    if (Number.isFinite(n)) return wrap(nf.format(n));
    return String(v);
  };

  if (style === "currency") {
    const nf = new Intl.NumberFormat("en-US", {
      style: "currency", currency, useGrouping: true,
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    });
    return (v) => numFmt(v, nf);
  }

  if (style === "percent") {
    const scale = fmt.percentScale ?? "unit";
    const nf = new Intl.NumberFormat("en-US", {
      style: "percent", useGrouping: true,
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    });
    return (v) => {
      if (v == null) return nullDisplay;
      let n = typeof v === "number" ? v : Number(v);
      if (!Number.isFinite(n)) return String(v);
      if (scale === "whole") n = n / 100;
      return wrap(nf.format(n));
    };
  }

  if (style === "compact") {
    const nf = new Intl.NumberFormat("en-US", {
      notation: "compact", compactDisplay: "short", useGrouping: true,
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    });
    return (v) => numFmt(v, nf);
  }

  if (style === "date" || style === "datetime" || style === "time") {
    const dfOpts: Intl.DateTimeFormatOptions = fmt.dateFormat
      ? parseDateFormatHint(fmt.dateFormat)
      : style === "datetime"
        ? { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
        : style === "time"
          ? { hour: "numeric", minute: "2-digit", second: "2-digit" }
          : { year: "numeric", month: "short", day: "numeric" };
    const df = new Intl.DateTimeFormat("en-US", dfOpts);
    return (v) => {
      if (v == null) return nullDisplay;
      const d = v instanceof Date ? v : new Date(typeof v === "number" || typeof v === "string" ? v : String(v));
      return Number.isNaN(d.getTime()) ? String(v) : wrap(df.format(d));
    };
  }

  // Default: plain number (covers "number", "int", "decimal", and unspecified)
  const nf = new Intl.NumberFormat("en-US", {
    useGrouping: true,
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  });
  return (v) => numFmt(v, nf);
}

/**
 * Convenience: takes a raw format record (from JSON spec) and returns a formatter,
 * or null if the input is falsy.
 */
export function buildFormatterFromRecord(raw: Record<string, unknown> | null | undefined): ((value: unknown) => string) | null {
  if (!raw || typeof raw !== "object") return null;
  return buildFormatter(raw as AtlasValueFormat);
}

function parseDateFormatHint(hint: string): Intl.DateTimeFormatOptions {
  const opts: Intl.DateTimeFormatOptions = {};
  const h = hint.toLowerCase();
  if (h.includes("yyyy") || h.includes("year")) opts.year = "numeric";
  if (h.includes("mmmm") || h.includes("month")) opts.month = "long";
  else if (h.includes("mmm")) opts.month = "short";
  else if (h.includes("mm")) opts.month = "2-digit";
  if (h.includes("dd") || h.includes("day")) opts.day = "numeric";
  if (h.includes("hh") || h.includes("hour")) opts.hour = "numeric";
  if (h.includes("mi") || h.includes("minute")) opts.minute = "2-digit";
  if (h.includes("ss") || h.includes("second")) opts.second = "2-digit";
  if (Object.keys(opts).length === 0) {
    opts.year = "numeric";
    opts.month = "short";
    opts.day = "numeric";
  }
  return opts;
}
